import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout/Layout';
import {
  Activity, Users, FileText, AlertTriangle, CheckCircle, Clock,
  TrendingUp, TrendingDown, Server, Database, Zap, Shield, BarChart3, LineChart as LineChartIcon
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { LiveActivityFeed } from '../components/Admin/LiveActivityFeed';
import { AdvancedMetrics } from '../components/Admin/AdvancedMetrics';
import { SmartAlertSystem } from '../components/Admin/SmartAlertSystem';
import { RealtimeCharts } from '../components/Admin/RealtimeCharts';
import { SystemResourcesMonitor } from '../components/Admin/SystemResourcesMonitor';
import { ActiveUsersMonitor } from '../components/Admin/ActiveUsersMonitor';
import { ESSProjectImporter } from '../components/Admin/ESSProjectImporter';

interface SystemMetrics {
  total_users: number;
  active_users_today: number;
  total_projects: number;
  projects_this_month: number;
  total_documents: number;
  pending_verifications: number;
  total_meetings: number;
  avg_response_time: number;
}

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  message: string;
  timestamp: string;
}

export const AdminDashboardPage: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics>({
    total_users: 0,
    active_users_today: 0,
    total_projects: 0,
    projects_this_month: 0,
    total_documents: 0,
    pending_verifications: 0,
    total_meetings: 0,
    avg_response_time: 0
  });
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'activity' | 'resources' | 'ess_data'>('overview');

  useEffect(() => {
    loadSystemMetrics();
    loadAlerts();
    loadActivityData();
  }, []);

  const loadSystemMetrics = async () => {
    try {
      setIsLoading(true);

      // Total users
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // Active users today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: activeToday } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('last_sign_in_at', today.toISOString());

      // Total projects
      const { count: totalProjects } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true });

      // Projects this month
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const { count: projectsThisMonth } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', firstDayOfMonth.toISOString());

      // Total documents
      const { count: totalDocuments } = await supabase
        .from('project_documents')
        .select('*', { count: 'exact', head: true });

      // Pending verifications
      const { count: pendingVerifications } = await supabase
        .from('project_documents')
        .select('*', { count: 'exact', head: true })
        .eq('verification_status', 'PENDING');

      // Total meetings
      const { count: totalMeetings } = await supabase
        .from('meetings')
        .select('*', { count: 'exact', head: true });

      setMetrics({
        total_users: totalUsers || 0,
        active_users_today: activeToday || 0,
        total_projects: totalProjects || 0,
        projects_this_month: projectsThisMonth || 0,
        total_documents: totalDocuments || 0,
        pending_verifications: pendingVerifications || 0,
        total_meetings: totalMeetings || 0,
        avg_response_time: 245
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAlerts = async () => {
    const systemAlerts: Alert[] = [];

    // Check for critical alerts
    if (metrics.pending_verifications > 10) {
      systemAlerts.push({
        id: '1',
        type: 'warning',
        message: `${metrics.pending_verifications} documents en attente de vérification`,
        timestamp: new Date().toISOString()
      });
    }

    // Check for unassigned projects
    const { count: unassignedProjects } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .is('assigned_advisor_id', null)
      .neq('status', 'TERMINE')
      .neq('status', 'REJETE');

    if (unassignedProjects && unassignedProjects > 0) {
      systemAlerts.push({
        id: '2',
        type: 'warning',
        message: `${unassignedProjects} projets sans accompagnateur`,
        timestamp: new Date().toISOString()
      });
    }

    // Check for pending advisor approvals
    const { count: pendingAdvisors } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('profile_type', 'ACCOMPAGNATEUR')
      .eq('advisor_status', 'PENDING');

    if (pendingAdvisors && pendingAdvisors > 0) {
      systemAlerts.push({
        id: '3',
        type: 'info',
        message: `${pendingAdvisors} demandes d'accompagnateur en attente`,
        timestamp: new Date().toISOString()
      });
    }

    setAlerts(systemAlerts);
  };

  const loadActivityData = async () => {
    // Generate activity data for the last 7 days
    const data = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);

      // Count projects created
      const { count: projects } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', date.toISOString())
        .lt('created_at', nextDay.toISOString());

      // Count users registered
      const { count: users } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', date.toISOString())
        .lt('created_at', nextDay.toISOString());

      data.push({
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        projects: projects || 0,
        users: users || 0
      });
    }
    setActivityData(data);
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'info':
        return <Activity className="w-5 h-5 text-blue-600" />;
      default:
        return <Activity className="w-5 h-5 text-gray-600" />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-900';
      case 'warning':
        return 'bg-orange-50 border-orange-200 text-orange-900';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  const userDistribution = [
    { name: 'Porteurs', value: Math.floor(metrics.total_users * 0.6), color: '#3b82f6' },
    { name: 'Accompagnateurs', value: Math.floor(metrics.total_users * 0.25), color: '#10b981' },
    { name: 'Financeurs', value: Math.floor(metrics.total_users * 0.1), color: '#f59e0b' },
    { name: 'Coordinateurs', value: Math.floor(metrics.total_users * 0.05), color: '#8b5cf6' }
  ];

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Vue d\'ensemble', icon: Activity },
    { id: 'metrics' as const, label: 'Métriques avancées', icon: BarChart3 },
    { id: 'activity' as const, label: 'Activité en direct', icon: LineChartIcon },
    { id: 'resources' as const, label: 'Ressources système', icon: Server },
    { id: 'ess_data' as const, label: 'Données ESS', icon: Database },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Monitoring Système</h1>
          <p className="text-gray-600 mt-2">Supervision en temps réel de la plateforme</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2">
          <div className="flex space-x-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'overview' && (
          <>
            {/* System Health Status */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Système opérationnel</h3>
                    <p className="text-sm text-gray-600">Tous les services fonctionnent normalement</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Dernière mise à jour : {new Date().toLocaleTimeString('fr-FR')}</span>
                </div>
              </div>
            </div>

            {/* Smart Alert System */}
            <SmartAlertSystem />


        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Users */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-blue-600" />
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Utilisateurs Total</p>
            <p className="text-3xl font-bold text-gray-900">{metrics.total_users}</p>
            <p className="text-xs text-green-600 mt-2">
              {metrics.active_users_today} actifs aujourd'hui
            </p>
          </div>

          {/* Total Projects */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8 text-green-600" />
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Projets Total</p>
            <p className="text-3xl font-bold text-gray-900">{metrics.total_projects}</p>
            <p className="text-xs text-green-600 mt-2">
              +{metrics.projects_this_month} ce mois
            </p>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-2">
              <Database className="w-8 h-8 text-orange-600" />
              {metrics.pending_verifications > 5 ? (
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>
            <p className="text-sm text-gray-600 mb-1">Documents</p>
            <p className="text-3xl font-bold text-gray-900">{metrics.total_documents}</p>
            <p className={`text-xs mt-2 ${metrics.pending_verifications > 5 ? 'text-orange-600' : 'text-gray-600'}`}>
              {metrics.pending_verifications} en attente
            </p>
          </div>

          {/* Meetings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-8 h-8 text-purple-600" />
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Rendez-vous</p>
            <p className="text-3xl font-bold text-gray-900">{metrics.total_meetings}</p>
            <p className="text-xs text-gray-600 mt-2">
              Accompagnement actif
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activité (7 derniers jours)</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="projects" stroke="#3b82f6" name="Projets" strokeWidth={2} />
                <Line type="monotone" dataKey="users" stroke="#10b981" name="Utilisateurs" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* User Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition Utilisateurs</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={userDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {userDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* System Resources */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ressources Système</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Server className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Base de données</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-1">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: '42%' }} />
              </div>
              <p className="text-xs text-gray-600">42% utilisé</p>
            </div>

            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Zap className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Temps de réponse</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-1">
                <div className="bg-green-600 h-3 rounded-full" style={{ width: '25%' }} />
              </div>
              <p className="text-xs text-gray-600">{metrics.avg_response_time}ms moyen</p>
            </div>

            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Sécurité</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-600">SSL actif - RLS actif</span>
              </div>
            </div>
          </div>
        </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions Rapides</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => window.location.href = '/admin/users'}
                  className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-center"
                >
                  <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <span className="text-sm font-medium text-blue-900">Utilisateurs</span>
                </button>

                <button
                  onClick={() => window.location.href = '/admin/accompagnateurs'}
                  className="p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-center"
                >
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <span className="text-sm font-medium text-green-900">Accompagnateurs</span>
                </button>

                <button
                  onClick={() => window.location.href = '/admin/audit'}
                  className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-center"
                >
                  <FileText className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                  <span className="text-sm font-medium text-orange-900">Audit Logs</span>
                </button>

                <button
                  onClick={loadSystemMetrics}
                  className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-center"
                >
                  <Activity className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <span className="text-sm font-medium text-purple-900">Actualiser</span>
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'metrics' && <AdvancedMetrics />}

        {activeTab === 'activity' && (
          <>
            <ActiveUsersMonitor />
            <RealtimeCharts />
            <LiveActivityFeed />
          </>
        )}

        {activeTab === 'resources' && <SystemResourcesMonitor />}

        {activeTab === 'ess_data' && (
          <div className="space-y-6">
            <ESSProjectImporter />
          </div>
        )}
      </div>
    </Layout>
  );
};
